import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PhoneCall, ChevronDown, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import QuoteModal from './QuoteModal';

const servicePages = [
  { name: 'Garage Epoxy Flooring', path: '/garage-epoxy' },
  { name: 'Warehouse Epoxy Flooring', path: '/warehouse-epoxy' },
  { name: 'Workshop Epoxy Flooring', path: '/workshop-epoxy' },
  { name: 'Commercial Epoxy Flooring', path: '/commercial-epoxy' },
  { name: 'Driveway Resurfacing', path: '/driveway-resurfacing' },
  { name: 'Metallic & Marble Epoxy', path: '/metallic-epoxy' },
  { name: 'Seamless Flake Flooring', path: '/flake-flooring' },
  { name: 'Shops & Showrooms Epoxy', path: '/shops-showrooms-epoxy' },
  { name: 'Office Epoxy Flooring', path: '/office-epoxy' },
  { name: 'Epoxy Flooring Sydney', path: '/epoxy-flooring-sydney' },
];

const Header = ({ activePage }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isServicePage = servicePages.some(s => s.path === `/${activePage}`);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-red-700/20" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-11">
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img src="/images/logo.png" alt="Alpha Epoxy Floors" className="h-20 w-auto" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center space-x-8" data-testid="desktop-nav">
              {activePage !== 'offers' && (
                <>
                  <Link to="/" className={`transition-colors duration-200 font-medium ${activePage === 'home' ? 'text-red-600 font-semibold' : 'text-gray-300 hover:text-red-600'}`}>Home</Link>

                  {/* Mega Menu Trigger */}
                  <div
                    className="relative"
                    onMouseEnter={() => setMegaOpen(true)}
                    onMouseLeave={() => setMegaOpen(false)}
                    data-testid="mega-menu-trigger"
                  >
                    <button className={`flex items-center gap-1 transition-colors duration-200 font-medium ${isServicePage ? 'text-red-600 font-semibold' : 'text-gray-300 hover:text-red-600'}`}>
                      Epoxy Flooring <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${megaOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {megaOpen && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-[560px]" data-testid="mega-menu-dropdown">
                        <div className="bg-blue-950/95 backdrop-blur-md border border-red-600/30 rounded-xl shadow-2xl shadow-red-600/10 p-6">
                          <p className="text-red-600 font-bold text-sm mb-4 uppercase tracking-wider">Our Services</p>
                          <div className="grid grid-cols-2 gap-2">
                            {servicePages.map((s) => (
                              <Link
                                key={s.path}
                                to={s.path}
                                className={`block px-4 py-2.5 rounded-lg text-sm transition-all duration-200 ${activePage === s.path.slice(1) ? 'bg-red-600/20 text-red-600 font-semibold' : 'text-gray-300 hover:bg-red-600/10 hover:text-red-600'}`}
                                data-testid={`mega-link-${s.path.slice(1)}`}
                              >
                                {s.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <Link to="/epoxy-flooring-sydney" className={`transition-colors duration-200 font-medium ${activePage === 'epoxy-flooring-sydney' ? 'text-red-600 font-semibold' : 'text-gray-300 hover:text-red-600'}`}>Epoxy Flooring Sydney</Link>
                  <Link to="/about" className={`transition-colors duration-200 font-medium ${activePage === 'about' ? 'text-red-600 font-semibold' : 'text-gray-300 hover:text-red-600'}`}>About Us</Link>
                  <Link to="/contact" className={`transition-colors duration-200 font-medium ${activePage === 'contact' ? 'text-red-600 font-semibold' : 'text-gray-300 hover:text-red-600'}`}>Contact</Link>
                </>
              )}

              <a href="tel:0451945455" className="flex items-center text-gray-300 hover:text-red-600 transition-colors duration-200 font-semibold" data-testid="header-phone">
                <PhoneCall className="w-4 h-4 mr-2 text-red-600" />
                0451 945 455
              </a>

              <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold transition-all duration-200" onClick={() => setIsModalOpen(true)} data-testid="header-get-quote-btn">
                Get Quote
              </Button>
            </nav>

            {/* Mobile Hamburger */}
            {activePage !== 'offers' && (
              <button className="lg:hidden text-white" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
                {mobileOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-black/95 border-t border-red-700/20 px-4 pb-6 pt-2" data-testid="mobile-menu">
            <Link to="/" className="block py-3 text-gray-300 hover:text-red-600" onClick={() => setMobileOpen(false)}>Home</Link>
            <p className="py-2 text-red-600 font-bold text-sm uppercase tracking-wider">Epoxy Flooring</p>
            {servicePages.map(s => (
              <Link key={s.path} to={s.path} className="block py-2 pl-4 text-gray-400 hover:text-red-600 text-sm" onClick={() => setMobileOpen(false)}>{s.name}</Link>
            ))}
            <Link to="/epoxy-flooring-sydney" className="block py-3 text-gray-300 hover:text-red-600 font-medium" onClick={() => setMobileOpen(false)}>Epoxy Flooring Sydney</Link>
            <Link to="/about" className="block py-3 text-gray-300 hover:text-red-600" onClick={() => setMobileOpen(false)}>About Us</Link>
            <Link to="/contact" className="block py-3 text-gray-300 hover:text-red-600" onClick={() => setMobileOpen(false)}>Contact</Link>
            <a href="tel:1300ALPHAEPOXY" className="flex items-center py-3 text-gray-300 hover:text-red-600 font-semibold">
              <PhoneCall className="w-4 h-4 mr-2 text-red-600" />0451 945 455
            </a>
            <Button className="w-full mt-3 bg-red-600 hover:bg-red-700 text-white font-semibold" onClick={() => { setIsModalOpen(true); setMobileOpen(false); }}>
              Get Quote
            </Button>
          </div>
        )}
      </header>

      <QuoteModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default Header;
